import type { ImageValue, Item, OptionValue, RefValue } from "./types.js";

/**
 * Coercion rules for Webflow's resolved field values.
 *
 * After the sync's reference-resolution pass a field is never simply a string:
 * a reference is `{id,name,slug}`, a multi-reference an array of those, an
 * option `{id,name}`, an image `{alt,url,fileId}`. Binding any of them naively
 * renders `[object Object]`, so every binding routes through here.
 */

export function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isImage(v: unknown): v is ImageValue {
  return isObject(v) && typeof v.url === "string";
}

export function isRef(v: unknown): v is RefValue | OptionValue {
  return isObject(v) && typeof v.id === "string";
}

/**
 * Reads a dotted path out of an item: `author.name`, `main-image.url`.
 * A bare field name returns the raw value, which the callers below coerce.
 */
export function resolvePath(item: Item, path: string): unknown {
  if (!path.includes(".")) return item[path];
  let current: unknown = item;
  for (const segment of path.split(".")) {
    if (current == null) return undefined;
    if (Array.isArray(current)) {
      const index = Number(segment);
      current = Number.isInteger(index) ? current[index] : undefined;
      continue;
    }
    if (!isObject(current)) return undefined;
    current = current[segment];
  }
  return current;
}

/**
 * The display string for a value.
 *
 * - reference / option → its `name`
 * - multi-reference    → every `name`, joined
 * - image              → its `alt` (an image has no sensible text form)
 * - scalar             → itself
 * - null / undefined   → '' (the caller hides the node)
 */
export function toText(value: unknown, separator = ", "): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .map((v) => toText(v, separator))
      .filter(Boolean)
      .join(separator);
  }
  if (isImage(value)) return value.alt ?? "";
  if (isRef(value)) return value.name ?? "";
  if (typeof value === "boolean" || typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return "";
}

/** The URL for an image field, or a value that is already a URL string. */
export function toSrc(value: unknown): string {
  if (isImage(value)) return value.url;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) return toSrc(value[0]);
  return "";
}

/** Alt text for an image field, falling back to its display string. */
export function toAlt(value: unknown): string {
  if (isImage(value)) return value.alt ?? "";
  return toText(value);
}

/** True when a value should hide its `data-slater-hide-empty` node. */
export function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "string") return value.trim() === "";
  return false;
}

/**
 * Fills `{field}` placeholders in a URL pattern, e.g. `/{locale}/blog/{slug}`.
 * `{locale}` is supplied separately since it is page-level, not item-level.
 */
export function interpolate(pattern: string, item: Item, locale: string | undefined): string {
  return pattern.replace(/\{([^}]+)\}/g, (_match, key: string) => {
    if (key === "locale") return locale ?? "";
    const value = resolvePath(item, key);
    // A reference in a URL means its slug, not its display name.
    const text = isRef(value) ? (value as RefValue).slug ?? value.id : toText(value);
    return encodeURIComponent(text);
  });
}
